/** Lightweight before/after slider for the landing page (no viewer, no workers). */
import { useRef, useState } from 'react';

export function BeforeAfter({ before, after, width, height, alt }: { before: string; after: string; width: number; height: number; alt: string }) {
  const [pos, setPos] = useState(0.5);
  const [dragging, setDragging] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const move = (clientX: number) => {
    const r = box.current!.getBoundingClientRect();
    setPos(Math.min(1, Math.max(0, (clientX - r.left) / r.width)));
  };
  return (
    <div
      ref={box}
      className="relative mx-auto w-full max-w-md touch-none select-none overflow-hidden rounded-xl shadow-lg"
      style={{ aspectRatio: `${width} / ${height}` }}
      onPointerDown={(e) => {
        setDragging(true);
        move(e.clientX);
      }}
      onPointerMove={(e) => dragging && move(e.clientX)}
      onPointerUp={() => setDragging(false)}
      onPointerLeave={() => setDragging(false)}
    >
      <img src={after} alt={`${alt}, restored`} width={width} height={height} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full" draggable={false} />
      <img
        src={before}
        alt={`${alt}, original scan`}
        width={width}
        height={height}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full"
        style={{ clipPath: `inset(0 ${(1 - pos) * 100}% 0 0)` }}
        draggable={false}
      />
      <span className="pointer-events-none absolute left-2 top-2 rounded bg-black/55 px-2 py-0.5 text-xs text-white">Before</span>
      <span className="pointer-events-none absolute right-2 top-2 rounded bg-black/55 px-2 py-0.5 text-xs text-white">After</span>
      <div
        role="slider"
        tabIndex={0}
        aria-label="Before and after divider"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pos * 100)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') setPos((p) => Math.max(0, p - 0.05));
          if (e.key === 'ArrowRight') setPos((p) => Math.min(1, p + 0.05));
        }}
        className="absolute top-0 bottom-0 w-8 -translate-x-1/2 cursor-ew-resize"
        style={{ left: `${pos * 100}%` }}
      >
        <div className="absolute left-1/2 top-0 bottom-0 w-0.5 -translate-x-1/2 bg-white shadow" />
        <div className="absolute left-1/2 top-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-xs text-black shadow-md">
          ◀▶
        </div>
      </div>
    </div>
  );
}
